import type { TFunction } from 'i18next'

import { convertAreaLabel } from '~/models/area'
import { isNumericKw } from '~/pages/CustomerInfo/models/kwInput'
import {
  CONTRACTOR_ERROR_I18N_KEY,
  ContractorError,
  SUPPLY_POINT_ERROR_FIELD,
  SUPPLY_POINT_ERROR_I18N_KEY,
  SupplyPointError,
  type SupplyPointErrorField,
  type SupplyPointErrorReason,
} from '~/pages/CustomerInfo/models/supplyPointError'

export type ResolvedSupplyPointError = {
  field: SupplyPointErrorField
  reason: SupplyPointErrorReason | undefined
  message: string
}

/** 画面の入力欄の名前 (クライアント側の検査用の欄を含む)。 */
export type FormErrorField = SupplyPointErrorField | 'supplyStart'

/** 番号が未定の見込みを番号のある需要家に足す時だけ、 別の文言を使う。 */
const messageKey = (error: SupplyPointError): string => {
  if (error.params.fixOthers === true) {
    if (error.reason === 'CUSTOMER_AREA_MISMATCH') {
      return 'page:customerInfo.error.customerAreaFixOthers'
    }
    if (error.reason === 'CUSTOMER_NUMBER_MISMATCH') {
      return 'page:customerInfo.error.customerNumberFixOthers'
    }
  }
  return error.reason === 'CUSTOMER_NUMBER_MISMATCH' &&
    error.params.numberBlank === true
    ? 'page:customerInfo.error.customerNumberBlank'
    : SUPPLY_POINT_ERROR_I18N_KEY[error.reason]
}

/** reason を欄の下 (field) またはモーダル上部 (root) に出す文言へ変換する。 */
export const resolveSupplyPointError = (
  error: unknown,
  t: TFunction,
): ResolvedSupplyPointError => {
  if (error instanceof SupplyPointError) {
    return {
      field: SUPPLY_POINT_ERROR_FIELD[error.reason],
      reason: error.reason,
      message: t(messageKey(error), {
        supplyPointNumber: error.params.supplyPointNumber,
        customerName: error.params.customerName,
        area:
          error.params.areaCode === undefined
            ? undefined
            : convertAreaLabel(error.params.areaCode),
      }),
    }
  }
  return {
    field: 'root',
    reason: undefined,
    message: t('page:customerInfo.error.unknown'),
  }
}

/**
 * kW の入力欄の文字列を Decimal 文字列として取り出す (number にしない: 桁が丸まり、 1e-7 のような指数表記になる)。
 * 空は undefined。 数値でない文字列もそのまま返し、 送る側 (`isNumericKw`) が判断する。
 */
export const parseKwInput = (value: string): string | undefined => {
  const trimmed = value.trim().replace(/,/g, '')
  return trimmed === '' ? undefined : trimmed
}

/**
 * kW の入力欄が「数として読めない」か。 正規化後 (`parseKwInput`) で判定する: 「,」 だけの入力は
 * 生の文字列では空でないが、 正規化すると空になり、 そのまま送ると「入力なし」として保存される。
 * 空欄 (空白のみ) は false (必須かどうかは項目ごとに別で見る)。
 */
export const isInvalidKwInput = (value: string): boolean => {
  if (value.trim() === '') return false
  const normalized = parseKwInput(value)
  return normalized === undefined || !isNumericKw(normalized)
}

/** 契約者の保存・削除の拒否を画面の文言にする。 */
export const resolveContractorError = (error: unknown, t: TFunction): string =>
  error instanceof ContractorError
    ? t(CONTRACTOR_ERROR_I18N_KEY[error.reason], {
        count: error.params.customerCount,
      })
    : t('page:customerInfo.error.unknown')
